import { getSupabaseClient } from "../supabase/index.js";
import {
  normalizePortalContactPhone,
  resolvePortalContactIdentifier,
} from "./contact-visibility.js";
import { callParentPortalRpcCached } from "./parent-portal-rpc-cache.js";

function asStr(v) {
  return String(v ?? "").trim();
}

function parseJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * @param {unknown} raw
 */
export function normalizeParentPortalContactIdentifier(raw) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const kind = asStr(raw.kind).toLowerCase();
    const value = asStr(raw.value);
    if ((kind === "email" || kind === "phone") && value) {
      return { kind, value };
    }
  }
  return { kind: "none", value: "" };
}

/**
 * @param {{ email?: unknown, phone?: unknown, portalContactIdentifier?: unknown }} input
 */
export function resolveParentPortalContactIdentifier(input) {
  const fromRpc = normalizeParentPortalContactIdentifier(
    input?.portalContactIdentifier,
  );
  if (fromRpc.kind !== "none") return fromRpc;
  return resolvePortalContactIdentifier({
    email: input?.email,
    phone: input?.phone,
  });
}

/**
 * @param {{ kind?: string, value?: string }} identifier
 */
export function parentPortalHasInvoiceIdentifier(identifier) {
  const kind = asStr(identifier?.kind).toLowerCase();
  const value = asStr(identifier?.value);
  return (kind === "email" || kind === "phone") && Boolean(value);
}

/**
 * @returns {Promise<{ ok: boolean, identifier?: { kind: string, value: string }, reason?: string }>}
 */
export async function loadParentPortalContactIdentifierRemote() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_parent_portal_resolve_contact_identifier",
    );
    if (error) return { ok: false, reason: "rpc-error", error };

    const identifier = normalizeParentPortalContactIdentifier(data);
    return { ok: true, identifier };
  } catch (error) {
    return { ok: false, reason: "exception", error };
  }
}

/**
 * @param {{ personId?: string|null }} [params]
 */
export async function loadParentPortalIdentifierConsolidatedStatementsRemote({
  personId = null,
} = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase", statements: [] };

    const res = await callParentPortalRpcCached(
      supabase,
      "trainerstunden_parent_portal_identifier_consolidated_statements",
      { p_person_id: personId ? asStr(personId) : null },
    );
    if (!res.ok) {
      return {
        ok: false,
        reason: res.reason || "rpc-error",
        error: res.error,
        statements: [],
      };
    }
    const data = res.data;
    if (data?.ok === false) {
      return {
        ok: false,
        reason: asStr(data?.reason) || "unknown-error",
        statements: [],
      };
    }

    return {
      ok: true,
      statements: parseJsonArray(data?.statements),
    };
  } catch (error) {
    return { ok: false, reason: "exception", error, statements: [] };
  }
}

/**
 * @param {object[]} primary
 * @param {object[]} additional
 */
export function mergeParentPortalStatementsById(primary, additional) {
  const byId = new Map();
  for (const row of [...(primary || []), ...(additional || [])]) {
    const id = asStr(row?.id);
    if (!id) continue;
    byId.set(id, row);
  }
  return [...byId.values()].sort((a, b) => {
    const sa = asStr(a?.sent_at) || asStr(a?.period_month);
    const sb = asStr(b?.sent_at) || asStr(b?.period_month);
    return sb.localeCompare(sa);
  });
}

/**
 * Profil-Fallback ohne RPC (Tests / leichte Payloads).
 *
 * @param {object|null|undefined} viewerProfile
 */
export function resolveParentPortalContactIdentifierFromProfile(viewerProfile) {
  return resolvePortalContactIdentifier({
    email: viewerProfile?.email,
    phone: viewerProfile?.phone,
  });
}

export { normalizePortalContactPhone };
